// ignore-string-externalization

import { createSelector } from 'reselect';
import { contentful } from '../lib/contentful';
import { setState } from './root';
import { ResponseError } from './utils/ResponseError';
import { makeContentfulAsset } from '../../../features/Contentful';
import { youtubeThumbnailUrl } from '../../../features/Template/socialCard';
import { youtubeIdRegex } from '../../../features/utils';

const isHighDPI = true; // typeof window === 'object' && window.devicePixelRatio > 1;

const categoriesKey = 'videoCategories';
const groupsKey = 'videoGroups';
const videosKey = 'videos';
const socialCardKey = 'socialCard';

const setLoadState = (key: $TSFixMe, items: $TSFixMe) =>
  setState({
    type: `${key}/LOAD`,
    payload: {
      [key]:
        key !== socialCardKey
          ? {
              byId: items.reduce(
                (acum: $TSFixMe, item: $TSFixMe) => ({
                  ...acum,
                  [item.id]: { ...item },
                }),
                {},
              ),
              allIds: items.map((item: $TSFixMe) => item.id),
              allSlugs: items.map((item: $TSFixMe) => item.urlSlug),
              slugToId: items.reduce(
                (acum: $TSFixMe, item: $TSFixMe) => ({
                  ...acum,
                  [item.urlSlug]: item.id,
                }),
                {},
              ),
            }
          : items,
    },
  });

const formatVideo = (item: $TSFixMe, json: $TSFixMe) => {
  const { id } = item.sys;
  const {
    releaseDate,
    thumbnailImage,
    videoUrl,
    headerBackgroundColor = '#000',
    ...video
  } = item.fields;
  const headerTextColor =
    headerBackgroundColor.toLowerCase() === '#000' ? '#fff' : '#000';

  const releaseDateParsed = new Date(releaseDate);
  // @ts-ignore
  if (releaseDateParsed > Date.now()) {
    return {};
  }

  const videoUrlParsed = youtubeIdRegex.exec(videoUrl);
  let thumbnail = json.includes.Asset.find(
    (asset: $TSFixMe) => asset.sys.id === thumbnailImage.sys.id,
  );
  const asset = thumbnail.fields;
  let header;
  if (thumbnail) {
    const {
      title,
      description,
      file: { url },
    } = asset;
    thumbnail = {
      title,
      description,
      url: `${url}?fm=jpg&fl=progressive&w=${764 / (isHighDPI ? 1 : 2)}`,
    };
    header = {
      title,
      description,
      url: `${url}?fm=jpg&fl=progressive&h=764`,
    };
  }

  return {
    [id]: {
      ...video,
      id,
      thumbnailAsset: asset,
      thumbnailImage: thumbnail,
      largeImage: header,
      youtubeId: videoUrlParsed && videoUrlParsed[1],
      releaseDate: releaseDateParsed.toISOString(),
      releaseDateUTC: releaseDateParsed.toUTCString(),
      headerBackgroundColor,
      headerTextColor,
      category: undefined, // will be filled in later when normalizing
    },
  };
};

const formatVideoPage = (item: $TSFixMe, json: $TSFixMe) => {
  const videoPage = item;
  const videoModule = json.includes.Entry.find(
    (entry: $TSFixMe) => entry.sys.id === videoPage.fields.video.sys.id,
  );

  if (!videoModule) {
    return {};
  }

  const videoUrl = videoModule.fields.videoUrl;
  const videoUrlParsed = youtubeIdRegex.exec(videoUrl);
  if (!(videoUrlParsed && videoUrlParsed[1])) {
    return {};
  }

  const name = videoPage.fields.title;
  const youtubeId = videoUrlParsed[1];

  let header;
  let asset;
  const thumbnailImage =
    videoPage.fields.thumbnailImage && videoPage.fields.thumbnailImage.sys.id;
  let thumbnail =
    thumbnailImage &&
    json.includes.Asset.find(
      (includedAsset: $TSFixMe) => includedAsset.sys.id === thumbnailImage,
    );
  if (thumbnail) {
    asset = thumbnail.fields;
    const {
      title,
      description,
      file: { url },
    } = asset;
    thumbnail = {
      title,
      description,
      url: `${url}?fm=jpg&fl=progressive&w=${764 / (isHighDPI ? 1 : 2)}`,
    };
    header = {
      title,
      description,
      url: `${url}?fm=jpg&fl=progressive&h=764`,
    };
  } else {
    const thumbnailUrl = youtubeThumbnailUrl(videoUrl);
    asset = makeContentfulAsset({
      url: thumbnailUrl as $TSFixMe,
      contentType: 'image/jpeg',
      width: 1280,
      height: 720,
      description: name,
    });
    thumbnail = {
      url: thumbnailUrl,
      description: name,
    };
    header = {
      url: thumbnailUrl,
      description: name,
    };
  }
  return {
    [item.sys.id]: {
      id: item.sys.id,
      name,
      urlSlug: videoPage.fields.urlSlug,
      body: videoPage.fields.description,
      thumbnailAsset: asset,
      thumbnailImage: thumbnail,
      largeImage: header,
      youtubeId,
      headerBackgroundColor: '#000',
      headerTextColor: '#fff',
      category: undefined, // will be filled in later when normalizing

      // this isn't consumed on the Hub Page but is by the legacy video page, so leaving to be safe
      videoDuration: '',
      translated: item.fields.translated || null,
    },
  };
};

const isVideo = (item: $TSFixMe) => item.sys.contentType.sys.id === 'video';
const isVideoPage = (item: $TSFixMe) =>
  item.sys.contentType.sys.id === 'videoPage';
const isVideoLike = (item: $TSFixMe) => isVideo(item) || isVideoPage(item);
const isAsset = (item: $TSFixMe) => item.sys.contentType.sys.id === 'Asset';

export const loadVideos =
  ({ smartlingLanguage = 'en-US', preview = false } = {}) =>
  async (dispatch: $TSFixMe) => {
    const response = await contentful.getEntries(
      {
        content_type: 'section',
        'fields.name': 'Videos',
        include: 10,
      },
      smartlingLanguage,
      preview,
    );

    function filterUntranslatedCategories(language: $TSFixMe, item: $TSFixMe) {
      if (language !== 'en-US' && !item.translated) {
        return false;
      }
      return true;
    }
    if (response.ok) {
      const json = await response.json();
      // videos
      const videosObject = json.includes.Entry.filter(isVideoLike).reduce(
        (acum: $TSFixMe, item: $TSFixMe) => {
          return {
            ...acum,
            ...(isVideo(item)
              ? formatVideo(item, json)
              : formatVideoPage(item, json)),
          };
        },
        {},
      );

      // groups
      const groupsObject = json.includes.Entry.filter(
        (item: $TSFixMe) => item.sys.contentType.sys.id === 'videoGroup',
      ).reduce((acum: $TSFixMe, item: $TSFixMe) => {
        const {
          sys: { id },
          fields,
        } = item;
        const videos = fields.aggregateOfAllVideos
          ? Object.values(videosObject).map(video => (video as $TSFixMe).id)
          : (fields.videos || [])
              .map((video: $TSFixMe) => video.sys.id)
              .filter((videoId: $TSFixMe) => videosObject[videoId] && videoId);

        return {
          ...acum,
          [id]: {
            ...fields,
            id,
            videos,
          },
        };
      }, {});

      // categories
      const categoriesWithVideos = json.items
        .filter(Boolean)[0]
        .fields.subSections.map(({ sys: { id: itemSysId } }: $TSFixMe) => {
          const item = json.includes.Entry.find(
            (entry: $TSFixMe) => entry.sys.id === itemSysId,
          );

          const { id } = item.sys;
          const { featuredVideo, subVideos, ...category } = item.fields;
          const groups = subVideos.map((group: $TSFixMe) => group.sys.id);
          const translated = item.fields.translated;

          return {
            ...category,
            id,
            translated,
            // only include the featured video if is available
            featuredVideo:
              videosObject[featuredVideo.sys.id] && featuredVideo.sys.id,
            groups,
            // temporary field to be able to assign a category to a video
            videos: groups.reduce((videosAcum: $TSFixMe, groupId: $TSFixMe) => {
              if (
                !groupsObject[groupId] ||
                groupsObject[groupId].aggregateOfAllVideos
              ) {
                return videosAcum;
              }

              groupsObject[groupId].videos.forEach((video: $TSFixMe) =>
                videosAcum.add(video),
              );
              return videosAcum;
            }, new Set()),
          };
        });

      const filteredCategories = categoriesWithVideos.filter((c: $TSFixMe) =>
        filterUntranslatedCategories(smartlingLanguage, c),
      );

      const categories = filteredCategories.map(
        ({ videos, ...category }: $TSFixMe) => category,
      );

      // normalize video groups
      const groups = Object.values(groupsObject);

      // normalize videos
      const videos = Object.values(videosObject).map((video: $TSFixMe) => ({
        ...video,
        category: (
          categoriesWithVideos.find((category: $TSFixMe) =>
            category.videos.has(video.id),
          ) || {}
        ).id,
      }));
      const socialCardId = json.items[0]?.fields.socialCard.sys.id;
      const socialCardAsset = json.includes.Asset.find(
        (asset: any) => asset.sys.id === socialCardId,
      );
      const socialCard = socialCardAsset?.fields.file.url;
      dispatch(setLoadState(socialCardKey, socialCard));
      dispatch(setLoadState(videosKey, videos));
      dispatch(setLoadState(groupsKey, groups));
      dispatch(setLoadState(categoriesKey, categories));
    } else {
      throw new ResponseError(response);
    }
  };

export const selectCategories = createSelector(
  state => (state as $TSFixMe)[categoriesKey],
  categories => (!categories ? undefined : categories),
);
export const selectGroups = createSelector(
  state => (state as $TSFixMe)[groupsKey],
  groups => (!groups ? undefined : groups),
);
export const selectVideos = createSelector(
  state => (state as $TSFixMe)[videosKey],
  videos => (!videos ? undefined : videos),
);

export const selectSocialCard = createSelector(
  state => (state as $TSFixMe)[socialCardKey],
  socialCard => (!socialCard ? undefined : socialCard),
);
